import React, { useEffect, useState } from "react";
import { X, Loader2, Wallet, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";

const money = (v) => `R ${Number(v || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * PaymentRecordingModal — record a payment against an issued Standalone Invoice.
 * Also lists prior payments with an "undo" button.
 *
 * props: invoice, onClose, onUpdated (fires with the updated invoice)
 */
export const PaymentRecordingModal = ({ invoice, onClose, onUpdated }) => {
  const [inv, setInv] = useState(invoice);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(Math.max(0, (inv.total || 0) - (inv.paid_total || 0)).toFixed(2));
  const [method, setMethod] = useState("eft");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const fresh = await adminApi.getInvoice(inv.id);
      // Coalesce nullable fields to safe defaults so controlled inputs stay controlled
      setInv({ ...fresh, notes: fresh.notes ?? "", reference: fresh.reference ?? "" });
      onUpdated && onUpdated(fresh);
    } catch { /* silent */ }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const record = async () => {
    const amt = Number(amount);
    if (!(amt > 0)) return toast.error("Enter an amount greater than R0");
    setBusy(true);
    try {
      await adminApi.addInvoicePayment(inv.id, {
        date, amount: amt, method,
        reference: reference.trim() || null,
        note: note.trim() || null,
      });
      toast.success(`Payment of ${money(amt)} recorded`);
      setReference(""); setNote(""); setAmount("0.00");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record payment");
    } finally { setBusy(false); }
  };

  const undo = async (pid) => {
    if (!window.confirm("Undo this payment?")) return;
    try {
      await adminApi.removeInvoicePayment(inv.id, pid);
      toast.success("Payment removed");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove payment"); }
  };

  const total = Number(inv.total || 0);
  const paid = Number(inv.paid_total || 0);
  const balance = Math.max(0, total - paid);
  const statusColor = {
    paid: "bg-[#ecfdf5] text-[#065f46] border-[#10b981]",
    part_paid: "bg-[#fef3c7] text-[#78350f] border-[#f59e0b]",
    unpaid: "bg-[#f5f6f6] text-[#3f4b52] border-[#cfd7db]",
    overdue: "bg-[#fef2f2] text-[#7f1d1d] border-[#dc2626]",
  }[inv.payment_status || "unpaid"];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="payment-modal">
      <div className="bg-white rounded-md shadow-xl max-w-2xl w-full p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Payment recording</div>
            <h2 className="font-display font-black uppercase text-xl text-[#111619] mt-1">
              Invoice {inv.invoice_number || "—"}
            </h2>
            <p className="text-xs text-[#3f4b52] mt-1">{inv.customer_name || "—"}</p>
          </div>
          <button onClick={onClose} className="text-[#3f4b52]" data-testid="payment-close-btn" aria-label="Close"><X size={18} /></button>
        </div>

        {/* Balance summary */}
        <div className="mt-4 grid grid-cols-3 gap-3">
          <Stat label="Total" value={money(total)} />
          <Stat label="Paid" value={money(paid)} />
          <Stat label="Balance" value={money(balance)} highlight />
        </div>
        <div className="mt-3">
          <span className={`inline-block border px-2 py-0.5 rounded-sm text-xs font-display uppercase tracking-wider ${statusColor}`} data-testid="payment-status-pill">
            {inv.payment_status || "unpaid"}
          </span>
        </div>

        {/* Prior payments */}
        {(inv.payments || []).length > 0 && (
          <div className="mt-5">
            <div className="text-xs font-display uppercase tracking-wider font-bold text-[#3f4b52] mb-1">Prior payments</div>
            <table className="w-full text-xs border border-[#cfd7db] rounded-md" data-testid="payments-table">
              <thead className="bg-[#f5f6f6] text-[#3f4b52]">
                <tr>
                  <th className="px-2 py-1.5 text-left">Date</th>
                  <th className="px-2 py-1.5 text-left">Method</th>
                  <th className="px-2 py-1.5 text-left">Reference</th>
                  <th className="px-2 py-1.5 text-right">Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eef2f4]">
                {(inv.payments || []).map((p) => (
                  <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                    <td className="px-2 py-1.5">{(p.date || "").slice(0, 10)}</td>
                    <td className="px-2 py-1.5 uppercase">{p.method}</td>
                    <td className="px-2 py-1.5">{p.reference || "—"}</td>
                    <td className="px-2 py-1.5 text-right font-bold">{money(p.amount)}</td>
                    <td className="px-2 py-1.5 text-right">
                      <button onClick={() => undo(p.id)} className="text-[#dc2626] hover:brightness-110" data-testid={`payment-remove-${p.id}`} aria-label="Undo payment">
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add payment form */}
        <div className="mt-5 border-t border-[#cfd7db] pt-4">
          <div className="text-xs font-display uppercase tracking-wider font-bold text-[#3f4b52] mb-2">Record a new payment</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Date"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={cls} data-testid="payment-date-input" /></F>
            <F label="Amount (ZAR)"><input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={cls} data-testid="payment-amount-input" /></F>
            <F label="Method">
              <select value={method} onChange={(e) => setMethod(e.target.value)} className={`${cls} bg-white`} data-testid="payment-method-select">
                <option value="eft">EFT</option>
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="cheque">Cheque</option>
                <option value="other">Other</option>
              </select>
            </F>
            <F label="Reference"><input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="EFT no. / receipt no." className={cls} data-testid="payment-reference-input" /></F>
            <F label="Note (optional)" span={2}>
              <input value={note} onChange={(e) => setNote(e.target.value)} className={cls} placeholder="Any internal comment…" />
            </F>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose}
            className="border border-[#cfd7db] text-[#3f4b52] font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md hover:bg-[#f5f6f6]"
            data-testid="payment-cancel-btn"
          >Close</button>
          <button onClick={record} disabled={busy || Number(amount) <= 0} data-testid="payment-record-btn"
            className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-5 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Wallet size={14} />}
            Record payment
          </button>
        </div>
      </div>
    </div>
  );
};

const cls = "w-full h-10 px-3 border border-[#cfd7db] rounded-md text-sm";
const F = ({ label, children, span = 1 }) => (
  <div className={span === 2 ? "sm:col-span-2" : ""}>
    <label className="text-xs font-display uppercase tracking-wider font-bold text-[#3f4b52] block mb-1">{label}</label>
    {children}
  </div>
);
const Stat = ({ label, value, highlight }) => (
  <div className={`border rounded-md p-3 ${highlight ? "border-[#f28a16] bg-[#fff8ea]" : "border-[#cfd7db] bg-white"}`}>
    <div className="text-xs font-display uppercase tracking-wider text-[#3f4b52]">{label}</div>
    <div className={`mt-1 font-bold ${highlight ? "text-[#f28a16] text-xl" : "text-[#111619]"}`}>{value}</div>
  </div>
);

export default PaymentRecordingModal;
